/**
 * CustomizableDashboard — a DashboardRenderer whose widget arrangement is
 * per-user customizable (reorder / resize / hide / show) and persisted.
 *
 * The page supplies a `catalog` of available widgets and a `defaultLayout`.
 * In edit mode each widget gets a control strip (move, resize, hide) and a
 * "＋ Add widget" picker lists hidden widgets. Save persists; Reset restores
 * the page default. Arrangement-only (no widget props) — see the hook.
 */
import React from 'react';
import {
  Pencil, Check, RotateCcw, EyeOff, ArrowUp, ArrowDown, Maximize2, Minimize2, Plus, X,
} from 'lucide-react';
import { DashboardProvider } from '../context/DashboardContext';
import { getWidget } from '../widgets/registry';
import { useDashboardLayout } from '../hooks/useDashboardLayout';

function EditControls({ entry, index, count, onMove, onHide, onSpan }) {
  const span = entry.colSpan ?? entry.def.colSpan ?? 1;
  const btn = 'rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30';
  return (
    <div className="mb-1 flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-gray-50 px-2 py-1">
      <span className="mr-auto truncate text-xs font-medium text-gray-600">{entry.def.title || entry.id}</span>
      <button type="button" className={btn} title="Move up" disabled={index === 0} onClick={() => onMove(entry.id, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
      <button type="button" className={btn} title="Move down" disabled={index === count - 1} onClick={() => onMove(entry.id, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
      <button type="button" className={btn} title={span === 2 ? 'Half width' : 'Full width'} onClick={() => onSpan(entry.id)}>
        {span === 2 ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
      </button>
      <button type="button" className={btn} title="Hide" onClick={() => onHide(entry.id)}><EyeOff className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export default function CustomizableDashboard({
  dashboardKey,
  module,
  siteId,
  time,
  departmentId,
  dimensions,
  fyStartMonth = 4,
  compare = false,
  standard = 'osha',
  refreshMs = 0,
  // Merged into the widget context as-is — for page-owned state a widget needs
  // (e.g. the site focus the site-comparison table sets).
  extraContext = null,
  catalog = [],
  defaultLayout = [],
  filterBar = null,
  actions = null,
  header = null,
  className = '',
}) {
  const {
    entries, editing, setEditing, dirty, loading, customised, move, toggleHide, cycleSpan, save, reset,
  } = useDashboardLayout({ dashboardKey, catalog, defaultLayout });

  const visible = entries.filter((e) => !e.hidden);
  const hidden = entries.filter((e) => e.hidden);

  // Expose the visible widget ids so export can mirror what's on the dashboard.
  const ctxValue = {
    module, siteId, time, departmentId, dimensions, fyStartMonth, compare, standard, refreshMs,
    visibleWidgetIds: visible.map((e) => e.id),
    ...(extraContext || {}),
  };

  // Layout controls (buttons only) — placed in the control bar's action cluster.
  const layoutButtons = editing ? (
    <>
      <button type="button" onClick={reset}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
        <RotateCcw className="h-4 w-4" /> Reset
      </button>
      <button type="button" onClick={save} disabled={!dirty}
        className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
        <Check className="h-4 w-4" /> Save
      </button>
      <button type="button" onClick={() => setEditing(false)}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
        <X className="h-4 w-4" /> Done
      </button>
    </>
  ) : (
    <>
      {/* Reset used to live INSIDE edit mode only. A user whose saved
          arrangement predates a change to the page default would then see the
          old, heavier dashboard with no visible way back — they had to guess
          that "Customize" hid the way out. Shown only when there IS a saved
          arrangement, so it never appears as a dead control. */}
      {customised && (
        <button type="button"
          onClick={() => {
            // Deleting the user's own saved arrangement is not undoable, so it
            // is confirmed rather than one stray click.
            if (window.confirm('Discard your saved arrangement for this dashboard and return to the default layout?')) {
              reset();
            }
          }}
          title="Return this dashboard to its default layout"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <RotateCcw className="h-4 w-4" /> Reset layout
        </button>
      )}
      <button type="button" onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
        <Pencil className="h-4 w-4" /> Customize
      </button>
    </>
  );

  // Unified control bar: data filters on the left, actions (saved views /
  // standard toggle / export) + layout buttons grouped on the right.
  const hasActions = Boolean(actions) || true;
  const controlBar = (filterBar || hasActions) && (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 rounded-xl border border-gray-200/70 bg-white px-4 py-3 shadow-sm">
      <div className="flex min-w-0 flex-1 flex-wrap items-end gap-3">{filterBar}</div>
      <div className="flex flex-wrap items-center gap-2">
        {actions}
        {actions && <span className="mx-1 hidden h-6 w-px bg-gray-200 sm:block" aria-hidden="true" />}
        {layoutButtons}
      </div>
    </div>
  );

  const renderWidget = (entry, index) => {
    const Comp = getWidget(entry.def.type);
    const span = (entry.colSpan ?? entry.def.colSpan ?? 1) === 2 ? 'lg:col-span-2' : '';
    return (
      <div key={entry.id} className={span}>
        {editing && (
          <EditControls entry={entry} index={index} count={visible.length} onMove={move} onHide={toggleHide} onSpan={cycleSpan} />
        )}
        {Comp ? <Comp {...(entry.def.props || {})} /> : (
          <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4 text-sm text-red-700">Unknown widget: {entry.def.type}</div>
        )}
      </div>
    );
  };

  return (
    <DashboardProvider value={ctxValue}>
      <div className={className}>
        {header}
        {controlBar}

        {loading ? (
          <div className="h-40 animate-pulse rounded-lg bg-gray-100" />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {visible.map((e, i) => renderWidget(e, i))}
          </div>
        )}

        {/* Hidden widgets picker (edit mode) */}
        {editing && hidden.length > 0 && (
          <div className="mt-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
            <div className="mb-2 text-sm font-medium text-gray-600">Hidden widgets — click to add back</div>
            <div className="flex flex-wrap gap-2">
              {hidden.map((e) => (
                <button key={e.id} type="button" onClick={() => toggleHide(e.id)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Plus className="h-3.5 w-3.5" /> {e.def.title || e.id}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardProvider>
  );
}
